import axios, { AxiosError } from 'axios';
import { useMutation, UseMutationOptions } from 'react-query';
import { api } from '@services/api/routes';
import { IPhysicalExamInfo } from '@pages/home/types';

interface ISavePhysicalExamResponse {
  firstName: string;
  lastName: string;
  dateOfBirth: string;
  email: string;
  verifyEmail: string;
  files: File[];
}

interface IPreSignedResponse {
  fileName: string;
  uploadUrl: string;
  fields: { [k: string]: string };
}

type TUseSavePhysicalExamOptions = UseMutationOptions<
  ISavePhysicalExamResponse,
  AxiosError,
  IPhysicalExamInfo
>;

const savePhysicalExamQueryKey = 'savePhysicalExamQueryKey';

async function savePhysicalExam(data: IPhysicalExamInfo) {
  const files = data.files.map(({ name, size }) => ({ name, size }));

  const {
    data: {
      requestId,
      items,
    },
  } = await api.generatePreSignedUrl({ files });

  const uploadPromises = items.map(async (item: IPreSignedResponse) => {
    const { fileName, uploadUrl, fields } = item;
    const file = data.files.find(f => f.name === fileName);

    if(!file) return;

    const form = new FormData();

    Object.entries(fields).forEach(([propName, value]) => {
      form.append(propName, value);
    });

    form.append('file', file);
    return axios.post(uploadUrl, form);
  });

  await Promise.all(uploadPromises);

  const formData = new FormData();

  formData.append('first_name', data.firstName);
  formData.append('last_name', data.lastName);
  formData.append('email', data.email);
  formData.append('dob', data.dateOfBirth);
  formData.append('requestId', requestId);

  const response = await api.savePhysicalExam(formData);

  return response.data;
}

const useSavePhysicalExam = (options?: TUseSavePhysicalExamOptions) =>
  useMutation<ISavePhysicalExamResponse, AxiosError, IPhysicalExamInfo>(
    savePhysicalExamQueryKey,
    savePhysicalExam,
    options,
  );

export default useSavePhysicalExam;
